import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
} from 'chart.js';
import { useEffect, useState } from 'react';
import { Bar } from 'react-chartjs-2';
import { nFitHistoryRecord } from '../../interfaces';
import { useAppDispatch } from '../../app/hooks';
import { selectScanResult } from '../../features/selected-scan/selected-scan-slice';
import { getNFitScoreHistory } from '../../services/resource-server/resource-server';
import { median, sigma } from '../../utilities/math';
import { getSelfReferencedScore } from '../../utilities/scoring-models';
import { convertDateForHistory } from '../../utilities/display-formats';

const NUM_BINS = 20;

// Overrides the globally registered StripedBgPlugin with a plain white background
const WhiteBgPlugin = {
    id: 'WhiteBg',
    beforeDraw: function (chart: any) {
        const ctx = chart.ctx;
        ctx.save();
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, chart.width, chart.height);
        ctx.restore();
    },
};

// Draws a vertical dashed red line at the most recent scan's bin position
const MostRecentScanPlugin = {
    id: 'MostRecentScan',
    afterDraw: function (chart: any) {
        const opts = chart.options.plugins?.mostRecentScan;
        if (!opts || opts.binIndex === undefined || opts.binIndex < 0) return;

        const meta = chart.getDatasetMeta(0);
        if (!meta || !meta.data || !meta.data[opts.binIndex]) return;

        const bar = meta.data[opts.binIndex];
        const ctx = chart.ctx;
        const chartArea = chart.chartArea;

        const score = opts.score !== undefined ? opts.score : null;
        const percentile = opts.percentile !== undefined ? opts.percentile : null;
        const color = opts.color ?? 'rgba(0, 0, 0, 0.9)';

        ctx.save();

        const labelHeight = 24;
        const lineTop = chartArea.top + labelHeight;

        // vertical line
        ctx.beginPath();
        ctx.strokeStyle = color;
        ctx.lineWidth = 2;
        ctx.moveTo(bar.x, lineTop);
        ctx.lineTo(bar.x, chartArea.bottom);
        ctx.stroke();

        // score label above the line, inside the chart area
        if (score !== null) {
            const label = percentile !== null
                ? `${score.toFixed(2)} (${Math.round(percentile)}%)`
                : score.toFixed(2);
            ctx.font = 'bold 14px Inter, sans-serif';
            ctx.fillStyle = color;
            ctx.textAlign = 'center';
            ctx.fillText(label, bar.x, chartArea.top + labelHeight - 4);
        }

        ctx.restore();
    },
};

const NormalPDFPlugin = {
    id: 'NormalPDF',
    afterDraw: function (chart: any) {
        const opts = chart.options.plugins?.normalPDF;
        if (!opts || !opts.enabled || !opts.sigma || opts.sigma === 0) return;

        const { mu, sigma, N, binWidth, firstBinCenter, lastBinCenter } = opts;

        const ctx = chart.ctx;
        const chartArea = chart.chartArea;
        const xScale = chart.scales.x;
        const yScale = chart.scales.y;

        const numBins = opts.numBins;
        const firstPx = xScale.getPixelForValue(0);
        const lastPx = xScale.getPixelForValue(numBins - 1);
        const scoreRange = lastBinCenter - firstBinCenter;

        const pixelToScore = (px: number): number =>
            firstBinCenter + ((px - firstPx) / (lastPx - firstPx)) * scoreRange;

        const scaledPDF = (x: number): number => {
            const z = (x - mu) / sigma;
            return (1 / (sigma * Math.sqrt(2 * Math.PI))) * Math.exp(-0.5 * z * z) * N * binWidth;
        };

        ctx.save();
        ctx.beginPath();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.75)';
        ctx.lineWidth = 2;

        const steps = 500;
        let started = false;
        for (let i = 0; i <= steps; i++) {
            const px = chartArea.left + (i / steps) * (chartArea.right - chartArea.left);
            const score = pixelToScore(px);
            const freq = scaledPDF(score);
            const py = yScale.getPixelForValue(freq);

            if (!started) {
                ctx.moveTo(px, py);
                started = true;
            } else {
                ctx.lineTo(px, py);
            }
        }
        ctx.stroke();

        // vertical line at mean, top intersects the PDF curve
        const meanPx = firstPx + ((mu - firstBinCenter) / scoreRange) * (lastPx - firstPx);
        const pdfAtMean = scaledPDF(mu);
        const pdfPeakPy = yScale.getPixelForValue(pdfAtMean);

        ctx.beginPath();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.9)';
        ctx.lineWidth = 2;
        ctx.moveTo(meanPx, pdfPeakPy);
        ctx.lineTo(meanPx, chartArea.bottom);
        ctx.stroke();

        // mean value label just above the intersection point
        const meanLabel = mu.toFixed(2);
        ctx.font = 'bold 14px Inter, sans-serif';
        ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
        ctx.textAlign = 'center';
        ctx.fillText(meanLabel, meanPx, pdfPeakPy - 6);

        // horizontal STD line from mean to mu+sigma on the PDF curve
        const sigmaPx = firstPx + ((mu + sigma - firstBinCenter) / scoreRange) * (lastPx - firstPx);
        const pdfAtSigma = scaledPDF(mu + sigma);
        const sigmaLinePy = yScale.getPixelForValue(pdfAtSigma);

        ctx.beginPath();
        ctx.strokeStyle = 'rgba(0, 0, 0, 0.9)';
        ctx.lineWidth = 2;
        ctx.moveTo(meanPx, sigmaLinePy);
        ctx.lineTo(sigmaPx, sigmaLinePy);
        ctx.stroke();

        // sigma label above the midpoint of the line
        const sigmaLabel = sigma.toFixed(2);
        ctx.font = 'bold 14px Inter, sans-serif';
        ctx.fillStyle = 'rgba(0, 0, 0, 0.85)';
        ctx.textAlign = 'center';
        ctx.fillText(sigmaLabel, meanPx + (sigmaPx - meanPx) / 2, sigmaLinePy - 6);

        ctx.restore();
    },
};

const ScanInfoPlugin = {
    id: 'ScanInfo',
    afterDraw: function (chart: any) {
        const opts = chart.options.plugins?.scanInfo;
        if (!opts || !opts.enabled) return;

        const ctx = chart.ctx;
        const { top, left } = chart.chartArea;
        ctx.save();
        ctx.font = '11px Inter, sans-serif';
        ctx.fillStyle = 'rgba(0, 0, 0, 0.6)';
        ctx.textAlign = 'left';
        if (opts.time) ctx.fillText(opts.time, left + 8, top + 16);
        if (opts.locale) ctx.fillText(opts.locale, left + 8, top + 32);
        ctx.restore();
    },
};

ChartJS.register(
    CategoryScale,
    LinearScale,
    BarElement,
    Title,
    Tooltip,
    Legend,
);

interface ChartProps {
    selfReferencingScore: boolean;
    startDate: string;
    onStatsReady?: (median: number, sigma: number) => void;
    onMostRecentScore?: (score: number) => void;
    onMostRecentBinColor?: (color: string) => void;
}

interface BinResult {
    labels: string[];
    counts: number[];
    binEdges: number[];
    binCenters: number[];
}

function buildBins(scores: number[], numBins: number): BinResult {
    if (scores.length === 0) return { labels: [], counts: [], binEdges: [], binCenters: [] };

    const minScore = Math.min(...scores);
    const maxScore = Math.max(...scores);
    const range = maxScore - minScore || 1;
    const binWidth = range / numBins;

    const binEdges: number[] = [];
    for (let i = 0; i <= numBins; i++) {
        binEdges.push(minScore + i * binWidth);
    }

    const counts: number[] = new Array(numBins).fill(0);
    for (const score of scores) {
        let idx = Math.floor((score - minScore) / binWidth);
        if (idx >= numBins) idx = numBins - 1;
        counts[idx]++;
    }

    const labels: string[] = [];
    const binCenters: number[] = [];
    for (let i = 0; i < numBins; i++) {
        const center = (binEdges[i] + binEdges[i + 1]) / 2;
        binCenters.push(center);
        labels.push(center.toFixed(2));
    }

    return { labels, counts, binEdges, binCenters };
}

function findBinIndex(score: number, binEdges: number[]): number {
    for (let i = 0; i < binEdges.length - 1; i++) {
        if (score >= binEdges[i] && score < binEdges[i + 1]) return i;
    }
    return binEdges.length - 2;
}

const quintileColors = ['#0C8140', '#75A377', '#C8C8CC', '#75ACD4', '#1376BC'];

function getQuintileColor(binCenter: number, quintileBoundaries: number[]): string {
    // boundaries = [p20, p40, p60, p80], higher score = greener
    if (binCenter >= quintileBoundaries[3]) return quintileColors[0];
    if (binCenter >= quintileBoundaries[2]) return quintileColors[1];
    if (binCenter >= quintileBoundaries[1]) return quintileColors[2];
    if (binCenter >= quintileBoundaries[0]) return quintileColors[3];
    return quintileColors[4];
}

function computeQuintileBoundaries(scores: number[]): number[] {
    const sorted = [...scores].sort((a, b) => a - b);
    const n = sorted.length;
    const percentile = (p: number) => sorted[Math.floor(p * n)] ?? sorted[n - 1];
    return [percentile(0.2), percentile(0.4), percentile(0.6), percentile(0.8)];
}

function computePercentileRank(value: number, scores: number[]): number {
    if (scores.length === 0) return 0;
    const below = scores.filter(s => s < value).length;
    const equal = scores.filter(s => s === value).length;
    return ((below + 0.5 * equal) / scores.length) * 100;
}


function NFitHistogramChart(props: ChartProps) {
    const { selfReferencingScore, startDate, onStatsReady, onMostRecentScore, onMostRecentBinColor } = props;
    const dispatch = useAppDispatch();

    const [rawData, setRawData] = useState<nFitHistoryRecord[]>([]);
    const [myMedian, setMyMedian] = useState<number>(0);
    const [mySigma, setMySigma] = useState<number>(0);
    const [chartData, setChartData] = useState<any>({ labels: [], datasets: [] });
    const [chartOptions, setChartOptions] = useState<any>({});

    useEffect(() => {
        const fetchData = async () => {
            const history = await getNFitScoreHistory(startDate);
            setRawData(history);
        };
        fetchData();
    }, [startDate]);

    useEffect(() => {
        const nFitArray = rawData.map(r => r.nFit);
        const calculatedMedian = median(nFitArray);
        const calculatedSigma = sigma(nFitArray);
        setMyMedian(calculatedMedian);
        setMySigma(calculatedSigma);
        onStatsReady?.(calculatedMedian, calculatedSigma);
    }, [rawData, onStatsReady]);

    useEffect(() => {
        if (rawData.length === 0) return;

        const getScore = (nFit: number) =>
            selfReferencingScore ? getSelfReferencedScore(nFit, myMedian, mySigma) : nFit;

        const isKnown = (deviceId?: string | null) => deviceId !== 'UNKNOWN';
        const mostRecentDeviceId = rawData[rawData.length - 1].deviceId;
        const filteredData = rawData.filter(r =>
            isKnown(mostRecentDeviceId) ? isKnown(r.deviceId) : !isKnown(r.deviceId)
        );

        const scores = filteredData.map(r => getScore(r.nFit));
        const { labels, counts, binEdges, binCenters } = buildBins(scores, NUM_BINS);
        const quintileBoundaries = computeQuintileBoundaries(scores);
        const barColors = binCenters.map(c => getQuintileColor(c, quintileBoundaries));

        const mostRecentRecord = filteredData[filteredData.length - 1];
        const mostRecentScore = getScore(mostRecentRecord.nFit);
        const mostRecentBinIdx = findBinIndex(mostRecentScore, binEdges);
        const mostRecentBinColor = getQuintileColor(binCenters[mostRecentBinIdx], quintileBoundaries);
        const mostRecentPercentile = computePercentileRank(mostRecentScore, scores);
        onMostRecentScore?.(mostRecentScore);
        onMostRecentBinColor?.(mostRecentBinColor);

        dispatch(selectScanResult({
            id: mostRecentRecord.id,
            date: convertDateForHistory(mostRecentRecord.date),
            nFit: mostRecentRecord.nFit,
            annotation: mostRecentRecord.annotation ?? '',
            riskFactors: mostRecentRecord.riskFactors ?? null,
        }));

        setChartData({
            labels,
            datasets: [
                {
                    label: 'Frequency',
                    data: counts,
                    backgroundColor: barColors,
                    borderColor: 'rgba(0, 0, 0, 0.8)',
                    borderWidth: 1,
                    barPercentage: 1.0,
                    categoryPercentage: 1.0,
                },
            ],
        });

        setChartOptions({
            responsive: true,
            layout: {
                padding: { top: 24 },
            },
            plugins: {
                legend: { display: false },
                title: { display: false },
                mostRecentScan: {
                    binIndex: mostRecentBinIdx,
                    score: mostRecentScore,
                    percentile: mostRecentPercentile,
                    color: 'rgba(0, 0, 0, 0.9)',
                },
                normalPDF: {
                    enabled: true,
                    mu: selfReferencingScore ? 0 : myMedian,
                    sigma: selfReferencingScore ? 1 : mySigma,
                    N: filteredData.length,
                    binWidth: binEdges.length > 1 ? binEdges[1] - binEdges[0] : 1,
                    firstBinCenter: binCenters[0],
                    lastBinCenter: binCenters[binCenters.length - 1],
                    numBins: NUM_BINS,
                },
                scanInfo: {
                    enabled: true,
                    time: mostRecentRecord.date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
                    locale: (mostRecentRecord.locale ?? '').replace(/[()]/g, '').replace('Local Time', '').trim(),
                },
            },
            scales: {
                x: {
                    title: {
                        display: true,
                        text: selfReferencingScore ? 'Self-Referenced Score' : 'nFit Score',
                        font: { family: 'Inter, sans-serif', size: 10 },
                    },
                    ticks: {
                        font: { family: 'Inter, sans-serif', size: 10 },
                        maxTicksLimit: 10,
                    },
                    grid: { display: false },
                },
                y: {
                    title: { display: false },
                    ticks: { display: false },
                    grid: { display: false },
                    beginAtZero: true,
                },
            },
        });
    }, [rawData, selfReferencingScore, myMedian, mySigma, dispatch, onMostRecentScore, onMostRecentBinColor]);

    return (
        <Bar
            options={chartOptions}
            data={chartData}
            plugins={[WhiteBgPlugin, NormalPDFPlugin, MostRecentScanPlugin, ScanInfoPlugin]}
            redraw={false}
        />
    );
}

export default NFitHistogramChart;
